<template>
    <div class="bg-white rounded-sm mx-2 my-3">
        <b-row>
            <b-col cols="12" class="mx-3 mt-3">
                <b-row>
                    <b-col>
                        <span class="title">Non Individual Registry File</span>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <b-row class="mx-3 mt-2" v-if="!hideBack">
            <b-col cols="12" class="mx-auto bg-header_grey py-3 rounded">
                <span class="cursor-pointer text-blue hover:text-blue-dark hover:underline" @click="back">Back</span>
                <span class="mx-3 text-gray">Non Individual / {{ resolvedErn }}</span>
            </b-col>
        </b-row>
        <b-row class="mx-3 mt-2" v-else>
            <b-col cols="12" class="mx-auto bg-header_grey py-2 rounded">
                <span class="text-gray font-bold text-sm">Non Individual Registry File / {{ resolvedErn || 'No ERN' }}</span>
            </b-col>
        </b-row>
        <b-row class="mx-3">
            <b-col cols="12" class="mx-auto my-3 shadow-sm rounded pt-2 pb-4 border border-blue">
                <b-row>
                    <b-col>
                        <b-row v-if="entity">
                            <b-col cols="12" class="mt-3">
                                <span class="font-bold text-blue h5">{{ entity.ern }}</span><br/>
                                <span class="font-bold text-gray h3">{{ entity.name }}</span><br/>
                                <div class="flex mt-1">
                                    <b-badge class="bg-blue text-capitalize">
                                        {{ entity.type }}
                                    </b-badge>
                                    <b-badge class="mx-1 bg-green text-capitalize" v-if="entity.subType">
                                        {{ entity.subType }}
                                    </b-badge>
                                </div>
                                <span class="italic text-gray text-xs font-bold" v-if="entity.createdAt">Registration Date: {{ dateFormat(entity.createdAt) }}</span><br/>
                                <span class="italic text-gray text-xs font-bold" v-if="entity.registrationInstitution">Associate Register: {{ entity.registrationInstitution }}</span><br/>
                            </b-col>
                        </b-row>
                        <b-row v-else-if="!resolvedErn">
                            <b-col cols="12" class="my-5 text-center text-gray-500 py-8">
                                <i class="fa fa-info-circle text-3xl mb-2 text-gray-400"></i>
                                <p class="text-sm font-semibold">No ERN provided for this application.</p>
                            </b-col>
                        </b-row>
                        <b-row v-else>
                            <b-col cols="12" class="my-3 blink_me">
                                <b-badge class="bg-gray_loading text-gray_loading my-1 w-2/12">ern</b-badge>
                                <br/>
                                <b-badge class="bg-gray_loading text-gray_loading py-3 w-4/12">name</b-badge>
                                <div class="flex mt-1">
                                    <b-badge class="bg-gray_loading">loading...</b-badge>
                                    <b-badge class="mx-1 bg-gray_loading">loading...</b-badge>
                                </div>
                                <b-badge class="bg-gray_loading my-1 text-gray_loading w-6/12">date</b-badge>
                                <br/>
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <RegistryFormDataViewer 
                                  :form-data="viewerFormData" 
                                  :value-data="viewerValueData" 
                                  :affiliations="affiliations"
                                  :api="apiInstance"
                                />
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import RegistryFormDataViewer from "./RegistryFormDataViewer.vue";
import { api } from "../../../../../app/services/api.service";
import moment from "moment";
import router from "@/router";

export default {
    name: "NirEntityViewPage",
    components: {
        RegistryFormDataViewer
    },
    props: {
        ern: {
            type: String,
            default: ''
        },
        hideBack: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            entity: null,
            viewerFormData: null,
            viewerValueData: [],
            affiliations: []
        };
    },
    computed: {
        resolvedErn() {
            return this.ern || (this.$route && this.$route.query.ern);
        },
        apiInstance() {
            return api();
        }
    },
    watch: {
        resolvedErn: {
            handler(newErn) {
                if (newErn) {
                    this.fetchEntityDetails(newErn);
                } else {
                    this.entity = null;
                    this.viewerFormData = null;
                    this.viewerValueData = [];
                    this.affiliations = [];
                }
            },
            immediate: true
        }
    },
    methods: {
        back() {
            router.go(-1);
        },
        dateFormat(date) {
            return moment(date).format("DD-MM-YYYY");
        },
        fetchEntityDetails(ern) {
            if (!ern) return;
            const self = this;
            api().get('/nir/api/v1/registry/file?ern=' + encodeURIComponent(ern))
                .then(function (res) {
                    if (res.data && res.data.data) {
                        self.entity = res.data.data;
                        if (res.data.data.entries) {
                            const entries = res.data.data.entries;
                            const fields = [];
                            const valueItems = [];
                            self.affiliations = res.data.data.affiliations || [];
                            
                            entries.forEach(function (entry, index) {
                                let type = 'text';
                                if (entry.fieldName === 'postal_address') type = 'postal-address';
                                else if (entry.fieldName === 'physical_address') type = 'admin-units';
                                else if (entry.fieldName === 'nature_of_business') type = 'isic';
                                else if (entry.fieldName === 'associates') type = 'associates';
                                else if (entry.fieldName === 'representatives') type = 'representatives';
                                else if (entry.fieldName === 'beneficial_owners') type = 'beneficial-owners';
                                else if (entry.fieldName === 'coporate_associates' || entry.fieldName === 'corporate_associates') type = 'corporate_associates';
                                else if (entry.fieldName === 'coporate_representatives' || entry.fieldName === 'corporate_representatives') type = 'corporate_representatives';
                                else if (entry.fieldName === 'official_email') type = 'email';
                                else if (entry.fieldName === 'official_telephone') type = 'contact';
                                else if (entry.fieldName.includes('contact')) type = 'contact';
                                
                                const fieldDef = {
                                    id: entry.fieldName,
                                    label: entry.label,
                                    type: type,
                                    position: index + 1
                                };
                                if (type === 'individuals' || type === 'associates' || type === 'representatives' || type === 'beneficial-owners' || type === 'corporate_associates' || type === 'corporate_representatives') {
                                    fieldDef.cardinality = 'multiple';
                                }
                                fields.push({ field: fieldDef });
                                
                                let parsedValue = entry.value;
                                if (parsedValue) {
                                    try {
                                        parsedValue = JSON.parse(parsedValue);
                                    } catch (e) {
                                      console.log(e);
                                    }
                                }
                                
                                valueItems.push({
                                    fieldId: entry.fieldName,
                                    fieldName: entry.fieldName,
                                    value: parsedValue
                                });
                            });
                            
                            self.viewerFormData = {
                                sections: [{
                                    id: 'entity-details-section',
                                    name: 'Registry Information',
                                    description: 'Details retrieved from the registry',
                                    position: 1,
                                    fields: fields
                                }]
                            };
                            
                            self.viewerValueData = [{ data: valueItems }];
                        }
                    }
                });
        }
    }
};
</script>

<style scoped>
.title {
    font-size: 1.5rem;
    font-weight: 600;
}
.blink_me {
    animation: blinker 3s linear infinite;
}

@keyframes blinker {
    80% {
        opacity: 0.3;
    }
}
</style>
